/**
 * Blueprint section-schema lock helpers for the Documents editor.
 *
 * The `blueprint` document type is hard-locked against `person_schema.json`
 * (Phase 7 / ADR-002): content *within* a section is free-form, but the set of
 * sections may not change without explicit provider approval. The backend
 * enforces this (`SchemaLockViolation` → 422); these helpers let the UI surface
 * the gate up-front — disabling structural edits and prompting for the
 * provider-approval flag when the section set would change — so the provider
 * understands why a commit needs approval rather than just seeing a 422.
 */
import { SECTION_CONFIGS } from "../blueprint/sections";

/** The canonical locked blueprint section keys (plus the metadata block). */
export const LOCKED_SECTION_KEYS: ReadonlySet<string> = new Set([
  ...SECTION_CONFIGS.map((c) => c.key),
  "metadata",
]);

/**
 * Normalize a typed section title into a stable, schema-style section key.
 *
 * Lower-cases, trims, and collapses any run of non-alphanumeric characters to a
 * single underscore (so "Family History!" → `family_history`), matching the
 * snake_case keys the blueprint content map uses.
 *
 * @param title - The raw title typed by the provider.
 * @returns The derived section key, or an empty string when nothing usable.
 */
export function toSectionKey(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Whether a document type's section set is structurally locked.
 *
 * @param docType - The document type (`blueprint` is locked; others are free).
 * @returns True when section additions/removals require provider approval.
 */
export function isStructurallyLocked(docType: string): boolean {
  return docType === "blueprint";
}

/**
 * Detect whether an edit changes the section *set* (a structural change).
 *
 * Adding or removing a section key — versus editing within existing sections —
 * is the structural change the schema lock governs.
 *
 * @param committed - The current committed content (section-keyed).
 * @param edited - The edited content (section-keyed).
 * @returns True when the set of section keys differs.
 */
export function isStructuralChange(
  committed: Record<string, string>,
  edited: Record<string, string>,
): boolean {
  const a = Object.keys(committed).sort();
  const b = Object.keys(edited).sort();
  if (a.length !== b.length) return true;
  return a.some((key, i) => key !== b[i]);
}

/**
 * Whether a blueprint commit requires the provider-approval flag.
 *
 * @param docType - The document type.
 * @param committed - The current committed content.
 * @param edited - The edited content.
 * @returns True when a structural change to a locked document needs approval.
 */
export function requiresProviderApproval(
  docType: string,
  committed: Record<string, string>,
  edited: Record<string, string>,
): boolean {
  return isStructurallyLocked(docType) && isStructuralChange(committed, edited);
}
